// The paste-an-email form.
//
// This is the whole replacement for the Outlook integration the sibling product
// had. There is no mailbox connection here, so the employee's message arrives the
// way HR already has it: selected in their mail client and pasted in.
//
// Body is the only required field. From and Subject improve the draft (the reply's
// To and Re: line come from them) but a paste of just the message text is the
// common case and must not be blocked on filling in a form.

import { useEffect, useRef, useState } from "react";
import type { EmailContext } from "../types";

export function EmailModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (email: EmailContext) => void;
}) {
  const [from, setFrom] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Focus the field people actually came here to fill. Focusing "From" first would
  // make the common case — paste the message, send — a three-tab journey.
  useEffect(() => {
    bodyRef.current?.focus();
  }, []);

  // Escape closes. A modal that can only be dismissed by finding the button is the
  // kind of thing that makes people paste into the wrong box instead.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  function submit() {
    if (!body.trim()) return;
    onSubmit({ from: from.trim(), subject: subject.trim(), body: body.trim() });
  }

  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        // Only a click that STARTS on the backdrop dismisses. Using onClick instead
        // means a text selection that begins inside the textarea and ends outside it
        // closes the dialog and discards the paste.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="email-modal-title" ref={dialogRef}>
        <div className="modal-head">
          <h2 id="email-modal-title">Reply to an email</h2>
          <button type="button" className="icon-btn" onClick={onCancel} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <label htmlFor="em-body">The employee's message</label>
            <textarea
              id="em-body"
              ref={bodyRef}
              value={body}
              placeholder="Paste the email here…"
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="em-from">From (optional)</label>
            <input id="em-from" value={from} placeholder="Dana Reyes &lt;dana@example.com&gt;" onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="em-subject">Subject (optional)</label>
            <input id="em-subject" value={subject} placeholder="Question about PTO carryover" onChange={(e) => setSubject(e.target.value)} />
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={!body.trim()} onClick={submit}>
            Draft a reply
          </button>
        </div>
      </div>
    </div>
  );
}
